// Red61 Ticketing related code

/*
    When the website visitor selects a given performance from the calendar, the website always needs to make a call
    to VIA (via the caching wrapper) to load the detailed pricing information, which also provides ticket availability
    information for each priceband.

    If all pricebands in this performance have the seats boolean set to false, the website should update the flag in
    the performance table to be false. If any of them return true, then the website should update its flag to true in
    it’s performance table.

    This will ensure that the performance calendar then shows the current data (as seen by the wrapper’s cache, which
    might be lagging behind the live ticket availability count).

    -- https://apidocs.red61.com/7.4.0-rc1/guides/performance_sync.html
 */

import { apiGet, apiPost, fetchForm, setFormSubmitButtonEnabled } from "./api";
import { Analytics } from "./analytics";

let broadcastChannel;
const WARNING_AT_MINUTES_REMAINING = 3;
let basketExpirationTime = null;
let basketItemCount = 0;

function updateDisplayTimers () {
    if (basketExpirationTime !== null) {
        const nowTimestamp = (new Date()).getTime() / 1000;
        const secondsDiff = Math.round(basketExpirationTime - nowTimestamp);
        let minutesTillExpiration = Math.ceil(secondsDiff / 60);
        if (minutesTillExpiration < 0) {
            minutesTillExpiration = 0;

            if (displayedMessage === 'expiration-warning') {
                alertUserForBasket('expiration');
                didShowForExpiration = true;
            }
        }

        document.querySelectorAll('.basket-timer').forEach((element) => {
            let text;

            if (minutesTillExpiration <= 1) {
                text = `less than 1 minute`
            } else {
                text = `${minutesTillExpiration} minutes`;
            }

            element.innerText = text;
        });
    }

    updateFloatingCartButton();
}

let displayedMessage = null;
let didShowForWarning = false;
let didShowForExpiration = false;

async function alertUserForBasket(messageType) {
    //console.log('alertUserForBasket', messageType);

    const cartMessages = document.getElementById('cartMessages');
    // already visible? close it first

    if (cartMessages.classList.contains('!translate-y-0')) {
        if (displayedMessage === messageType) {
            // already the displayed message
            return;
        }

        cartMessages.classList.remove('!translate-y-0');
        cartMessages.classList.remove('!opacity-100');
        await (new Promise((resolve,reject) => {
            setTimeout(() => {
                resolve();
            }, 1000);
        }));
    }

    if (messageType) {
        // show correct message
        cartMessages.querySelectorAll('*[data-message]').forEach((element) => {
            if (element.getAttribute('data-message') === messageType) {
                element.classList.remove('hidden');
            }
            else {
                element.classList.add('hidden');
            }
        });

        // show panel
        cartMessages.classList.add('!translate-y-0')
        cartMessages.classList.add('!opacity-100');
    }

    displayedMessage = messageType;
}

function basketKeepAlive() {
    return apiGet('basket-keep-alive').then(async (resp) => {
        const json = await resp.json();

        updateBasketTimer(json.basketExpiry, json.basketItemCount, true);

        return resp;
    }).catch((err) => {
        console.error('Failed to keep basket alive', err);
    });
}

export function updateBasketTimer(newExpiration, itemCount, broadcast = true) {
    basketExpirationTime = newExpiration
    basketItemCount = itemCount;

    if (broadcast && broadcastChannel) {
        broadcastChannel.postMessage({
            expiry: basketExpirationTime,
            itemCount: basketItemCount,
        });
    }

    updateDisplayTimers();
}

function startBasketTimer() {
    if (broadcastChannel) {
        broadcastChannel.addEventListener('message', (event) => {
            const { expiry, itemCount } = event.data;
            updateBasketTimer(expiry, itemCount, false);
        });
    }

    let syncInProgress = false;
    let hasSynced = false;

    function syncBasketExpiry() {
        if (syncInProgress) {
            return Promise.resolve();
        }

        syncInProgress = true;

        return apiGet('sync-basket-expiry').then(async (res) => {
            if (!res.ok) {
                throw new Error(`Unexpected response (${res.status}) from sync-basket-expiry`);
            }

            const response = await res.json();

            hasSynced = true;
            updateBasketTimer(response.basketExpiry, response.basketItemCount, true);
        }).catch((err) => {
            // retried on next basketTimerFunc tick
            console.error('Failed to sync basket expiry, will retry', err);
        }).finally(() => {
            syncInProgress = false;
        });
    }

    const basketTimerFunc = () => {
        if (basketExpirationTime) {
            const nowTimestamp = (new Date()).getTime() / 1000;
            const secondsDiff = Math.round(basketExpirationTime - nowTimestamp);

            //console.log(`seconds remaining = ${secondsDiff}`)

            if (secondsDiff <= 0 && secondsDiff > -10) {
                // expired
                if (!didShowForExpiration) {
                    didShowForExpiration = true;
                    alertUserForBasket('expiration');
                }

                didShowForWarning = true;
                basketExpirationTime = null;
                basketItemCount = 0;
            }
            else if (secondsDiff > 0 && secondsDiff <= (60 * WARNING_AT_MINUTES_REMAINING)) {
                didShowForExpiration = false;

                if (!didShowForWarning) {
                    didShowForWarning = true;
                    alertUserForBasket('expiration-warning');
                }
            } else if (secondsDiff > (60 * WARNING_AT_MINUTES_REMAINING)) {
                // if our timer is ABOVE the warning limit, reset our flags
                didShowForExpiration = false;
                didShowForWarning = false;

                // close the message
                alertUserForBasket(null);
            }

            updateDisplayTimers();
        } else {
            didShowForExpiration = true;
            didShowForWarning = true;

            // After the first sync, updates arrive via ajax responses and the broadcast channel.
            if (!hasSynced) {
                syncBasketExpiry();
            }
        }
    };

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            syncBasketExpiry();
        }
    });

    setInterval(basketTimerFunc, 5000);
    syncBasketExpiry().then(basketTimerFunc);
}

function updateFloatingCartButton() {
    const floatingCartButton = document.getElementById('cartrage');
    if (floatingCartButton) {
        const itemCountContainer = floatingCartButton.querySelector('.item-count');
        itemCountContainer.innerText = basketItemCount;

        if (window.location.pathname.indexOf('/cart') === 0) {
            floatingCartButton.classList.add('hidden');
        } else {
            if (basketItemCount > 0) {
                floatingCartButton.classList.remove('hidden');
            } else {
                floatingCartButton.classList.add('hidden');
            }
        }
    }
}

$(document).ready(function () {
    if (window.BroadcastChannel) {
        broadcastChannel = new BroadcastChannel('basket');
    }

    // Basket update forms
    document.querySelectorAll('form[data-ticketing][name="basket-tickets-form"]').forEach((form) => {
        const quantityInputs = [...form.querySelectorAll('input[name^="qty"]')];

        form.addEventListener('submit', (event) => {
            event.preventDefault();
            const form = event.target;
            const errorEle = form.querySelector('.form-error');
            const errorEleContainer = form.querySelector('.form-error-container');
            errorEleContainer.classList.add('max-h-0');
            errorEle.innerText = '';

            const changeCount = [...form.querySelectorAll('input[name^="qty"]')].reduce((changeCount, ticketInput) => {
                const val = ticketInput.value.length ? parseInt(ticketInput.value) : 0;
                const original = parseInt(ticketInput.getAttribute('data-original'));

                return changeCount + (val === original ? 0 : 1);
            }, 0);

            if (!changeCount) {
                const eventContainer = form.closest('.cart-event-container');
                eventContainer.querySelectorAll('button[data-ticketing][name="editEventTickets"]').forEach((button) => {
                    button.click();
                });

                return;
            }

            fetchForm(form).then((resp) => {
                if (resp.itemRemovals.length) {
                    Analytics.removeBasketItems({
                        items: resp.itemRemovals,
                        amount:  resp.itemRemovals.reduce((carry, item) => carry + item.price, 0)
                    });
                }

                if (resp.itemAdditions.length) {
                    Analytics.addBasketItems({
                        items: resp.itemAdditions,
                        amount:  resp.itemAdditions.reduce((carry, item) => carry + item.price, 0)
                    });
                }

                window.location.reload();
            }).catch((err) => {
                errorEleContainer.classList.remove('max-h-0');
                errorEle.innerText = err?.response?.error ?? '';

                setFormSubmitButtonEnabled(event.target, true);
            });
        });

        form.addEventListener('change', (event) => {
            quantityInputs.forEach((ticketInput) => {
                const ticketError = form.querySelector(`.form-error`);
                const errorEleContainer = form.querySelector('.form-error-container');
                errorEleContainer.classList.add('max-h-0');
                ticketError.innerText = null;

                const val = ticketInput.value.length ? parseInt(ticketInput.value) : 0;
                const ticketStep = parseInt(ticketInput.step);

                if (val % ticketStep !== 0) {
                    ticketError.innerText = `Tickets must be purchased in multiples of ${ticketStep}`;
                    errorEleContainer.classList.remove('max-h-0');
                }
            });
        });
    });

    document.querySelectorAll('button[data-ticketing][name="removePerformance"]').forEach((button) => {
        button.addEventListener('click', (event) => {
            button.disabled = true;

            apiPost('remove-performance-from-basket', {
                performanceRef: button.getAttribute('data-performance'),
            }).then(async (resp) => {
                let body = await resp.json();
                updateBasketTimer(body.basketExpiry, body.basketItemCount);

                window.location.reload();
            });
        });
    });

    document.querySelectorAll('button[data-ticketing][name="removeVoucher"]').forEach((button) => {
        button.addEventListener('click', (event) => {
            button.disabled = true;

            apiPost('remove-voucher-from-basket', {
                voucherRef: button.getAttribute('data-voucher'),
            }).then(async (resp) => {
                let body = await resp.json();
                updateBasketTimer(body.basketExpiry, body.basketItemCount);

                window.location.reload();
            });
        });
    });

    document.querySelectorAll('button[data-ticketing][name="editEventTickets"]').forEach((button) => {
        button.addEventListener('click', () => {
            const eventContainer = button.closest('.cart-event-container');
            eventContainer.querySelectorAll('.form-toggle-hidden').forEach((ele) => ele.classList.toggle('hidden'));
            button.classList.toggle('hidden');
        });
    });

    document.querySelectorAll('button[data-ticketing][name="editEventVouchers"]').forEach((button) => {
        button.addEventListener('click', () => {
            document.querySelectorAll('form[data-ticketing][name="basket-voucher-form"]').forEach((form) => {
                form.querySelectorAll('.form-toggle-hidden').forEach((ele) => ele.classList.toggle('hidden'));
            });

            button.classList.toggle('hidden');
        });
    });

    document.querySelectorAll('form[data-ticketing][name="accessNoteForm"]').forEach((form) => {
        const errorEle = form.querySelector('.form-error');
        form.addEventListener('submit', (event) => {
            event.preventDefault();
            errorEle.innerText = null;

            // ensure at least 1 category selected
            const formData = new FormData(form);
            const categories = formData.getAll('accessibilityCategory[]')
            const note = formData.get('accessibilityNote');

            if (!categories.length && note.length) {
                errorEle.innerText = 'Please select one or more categories for your message';
                return;
            }

            fetchForm(form).then((resp) => {
                window.location.reload();
            }).catch((err) => {
                errorEle.innerText = err?.response?.error ?? '';
                setFormSubmitButtonEnabled(event.target, true);
            });
        });
    });

    document.querySelectorAll('button[data-ticketing][name="editAccessibilityNote"]').forEach((button) => {
        button.addEventListener('click', (event) => {
            const noteContainer = button.closest('.cart-event-container').querySelector('.accessibility-notes-container');
            noteContainer.classList.toggle('hidden');
        });
    });

    document.querySelectorAll('a[data-ticketing].checkout-button').forEach((checkoutButton) => {
        checkoutButton.addEventListener('click', (event) => {
            const analyticsData = window.AnalyticsData ?? [];
            for (let data of analyticsData) {
                if (data.type === 'view-cart') {
                    Analytics.beginCheckout(data.data);
                    break;
                }
            }
        });
    });

    const applyCodeForm = document.querySelector('form[data-ticketing][name="apply-code-form"]');
    if (applyCodeForm) {
        const codeInput = applyCodeForm.querySelector('input[name="code"]');
        const submitButton = applyCodeForm.querySelector('button[type="submit"]');
        const errorEle = applyCodeForm.querySelector('.form-error');
        const errorEleContainer = applyCodeForm.querySelector('.form-error-container');

        codeInput.addEventListener('change', (event) => {
            submitButton.parentElement.classList.remove('hidden');
        });

        applyCodeForm.addEventListener('submit', (event) => {
            event.preventDefault();
            const form = event.target;

            fetchForm(form).then((resp) => {
                errorEleContainer.classList.add('max-h-0');
                errorEle.innerText = '';
                window.location.reload();
            }).catch((err) => {
                errorEleContainer.classList.remove('max-h-0');
                errorEle.innerText = err?.response?.error ?? '';

                setFormSubmitButtonEnabled(form, true);
            });
        });
    }

    startBasketTimer();

    const closeCartMessages = document.getElementById('closeCartMessages');
    if (closeCartMessages) {
        closeCartMessages.addEventListener('click', () => {
            alertUserForBasket(null);
        });
    }

    const cartMessages = document.getElementById('cartMessages');
    if (cartMessages) {
        let continueBrowsingButton = cartMessages.querySelector('button.cart-msg-continue-browsing');
        if (continueBrowsingButton) {
            continueBrowsingButton.addEventListener('click', (event) => {
                alertUserForBasket(null);
                basketKeepAlive();
            });
        }
    }
});
